import { z } from 'zod'
import { roomConsentSchema } from './consent'
import {
  liveRoomCategoryValues,
  liveRoomUserRoomSchema,
  userRoomVisibilityValues,
} from './live-room-user'
import { reportReasonValues } from './report'
import { myRoomRequestStateValues } from './room-requests'

/** What an event's Live Room offers: nothing, only a head count, or open people with Hi. */
export const liveRoomModeValues = ['off', 'count', 'open'] as const
export type LiveRoomMode = (typeof liveRoomModeValues)[number]

/**
 * Who sees a person at a Live Room: `hidden` is only counted, `friends` shows them to their
 * friends, `open` also to everyone else checked in there who is old enough.
 */
export const liveRoomVisibilityValues = ['hidden', 'friends', 'open'] as const
export type LiveRoomVisibility = (typeof liveRoomVisibilityValues)[number]

/** How long the chosen visibility holds; `end` runs until the event is over. */
export const liveRoomDurationValues = ['1h', '2h', '4h', 'end'] as const
export type LiveRoomDuration = (typeof liveRoomDurationValues)[number]

/** How a person proved they are at the event. */
export const liveRoomCheckinMethodValues = ['location', 'qr'] as const
export type LiveRoomCheckinMethod = (typeof liveRoomCheckinMethodValues)[number]

/** A Hi between two people: sent by the viewer, or waved both ways. */
export const liveRoomWaveStateValues = ['none', 'sent', 'received', 'mutual'] as const

/** The fixed lines a Hi can carry; free text is never offered, so a Hi stays friendly. */
export const liveRoomComplimentValues = ['hi', 'style', 'energy', 'smile', 'dance', 'vibe'] as const
export type LiveRoomCompliment = (typeof liveRoomComplimentValues)[number]
export type LiveRoomWaveState = (typeof liveRoomWaveStateValues)[number]

/** What a Hi from a Live Room carries as a chat message once both said Hi. */
export const roomHiMessageMetadataSchema = z.object({
  placeId: z.string().uuid(),
  eventId: z.string().uuid(),
  eventTitle: z.string(),
  /** One of `liveRoomComplimentValues`, kept loose so a later compliment never breaks a card. */
  compliment: z.string().nullable(),
  complimentText: z.string().nullable(),
})
export type RoomHiMessageMetadata = z.infer<typeof roomHiMessageMetadataSchema>

export const liveRoomPersonSchema = z.object({
  userId: z.string().uuid(),
  displayName: z.string().nullable(),
  username: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  /** Shown next to the name unless the person hides it. */
  age: z.number().int().nullable(),
  /** Links the person shows on their profile. */
  instagram: z.string().nullable(),
  linkedin: z.string().nullable(),
  /** The selfie of the moment, a signed URL valid for about an hour. */
  selfieUrl: z.string().nullable(),
  visibility: z.enum(liveRoomVisibilityValues),
  isFriend: z.boolean(),
  /** False when the person shows themselves but takes no Hi; friends still reach them. */
  acceptsHi: z.boolean(),
  wave: z.enum(liveRoomWaveStateValues),
  /** What came with a Hi the viewer got and has not answered yet. */
  receivedCompliment: z.enum(liveRoomComplimentValues).nullable().optional(),
  /** The words of a self-written compliment that came with the Hi. */
  receivedComplimentText: z.string().nullable().optional(),
  /** The direct chat a mutual Hi opened. */
  conversationId: z.string().uuid().nullable(),
  presentSince: z.string(),
  /** When the person last had the room open; separate from how long they stay visible. */
  lastActiveAt: z.string().nullable().optional(),
  /** Where the person stands with the viewer's group: a member, invited and not answered yet, or neither. */
  groupState: z.enum(['member', 'invited']).nullable().default(null),
  /** Joined on the web without an account: no friend requests. */
  isGuest: z.boolean().default(false),
})
export type LiveRoomPerson = z.infer<typeof liveRoomPersonSchema>

export const liveRoomEventSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  imageUrl: z.string().nullable(),
  venueName: z.string().nullable(),
  street: z.string().nullable(),
  city: z.string().nullable(),
  startTime: z.string(),
  endTime: z.string().nullable(),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  /** Set when a person opened this room themselves instead of an organizer. */
  userRoom: liveRoomUserRoomSchema.nullish(),
  /** People get in only once the host accepted their request ("Auf Anfrage"). */
  onRequest: z.boolean().optional(),
  /** Set once the organizer ended the room early; nobody checks in again. */
  roomClosedAt: z.string().nullable().optional(),
  /** Set on a room a venue's QR opened for the night: the venue it belongs to. */
  venue: z.object({ listingId: z.string().uuid(), name: z.string() }).nullable().default(null),
})
export type LiveRoomEvent = z.infer<typeof liveRoomEventSchema>

export const liveRoomComingPersonSchema = z.object({
  userId: z.string().uuid(),
  displayName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  status: z.enum(['going', 'maybe']),
})
export type LiveRoomComingPerson = z.infer<typeof liveRoomComingPersonSchema>

/** Who said they come and is not checked in yet: friends by face, everyone else as a count. */
export const liveRoomComingSchema = z.object({
  friends: z.array(liveRoomComingPersonSchema),
  others: z.number().int().nonnegative(),
})
export type LiveRoomComing = z.infer<typeof liveRoomComingSchema>

export const ANNOUNCEMENT_MAX_LENGTH = 200
export const ANNOUNCEMENT_COOLDOWN_MINUTES = 10
export const ANNOUNCEMENTS_PER_ROOM = 10

/** A short text the room's host sent to everyone inside. */
export const liveRoomAnnouncementSchema = z.object({
  id: z.string().uuid(),
  text: z.string(),
  senderName: z.string(),
  createdAt: z.string(),
})
export type LiveRoomAnnouncement = z.infer<typeof liveRoomAnnouncementSchema>

export const liveRoomAnnouncementCreateSchema = z.object({
  text: z.string().trim().min(1).max(ANNOUNCEMENT_MAX_LENGTH),
})
export type LiveRoomAnnouncementCreateInput = z.infer<typeof liveRoomAnnouncementCreateSchema>

/** The announcement just sent and how many people it reached. */
export const liveRoomAnnouncementResponseSchema = z.object({
  announcement: liveRoomAnnouncementSchema,
  recipientCount: z.number().int().nonnegative(),
})
export type LiveRoomAnnouncementResponse = z.infer<typeof liveRoomAnnouncementResponseSchema>

/**
 * A Live Room as the viewer may see it. `openPeople` is filled only for a viewer who is checked
 * in and old enough for open visibility; friends show wherever the viewer is.
 */
export const liveRoomSchema = z.object({
  event: liveRoomEventSchema,
  placeId: z.string().uuid().nullable(),
  mode: z.enum(liveRoomModeValues),
  /** Inside the window people can check in: 90 minutes before the start until an hour after. */
  open: z.boolean(),
  /** The event is running right now. */
  live: z.boolean(),
  counts: z.object({
    here: z.number().int().nonnegative(),
    open: z.number().int().nonnegative(),
  }),
  viewer: z.object({
    present: z.boolean(),
    checkedInVia: z.enum(liveRoomCheckinMethodValues).nullable(),
    visibility: z.enum(liveRoomVisibilityValues).nullable(),
    visibleUntil: z.string().nullable(),
    selfieUrl: z.string().nullable(),
    /** Whether others here may say Hi to the viewer. */
    acceptsHi: z.boolean(),
    /** Old enough to choose `open` and to see open people. */
    canBeOpen: z.boolean(),
    /** The viewer's group for tonight at this event, if any. */
    groupPlanId: z.string().uuid().nullable().default(null),
    /** Invitations to a group at this event the viewer has not answered yet, newest first. */
    groupInvites: z.array(z.lazy(() => liveRoomGroupInviteSchema)).default([]),
    /** The poster check-in link, while the viewer is checked in and the room is open. */
    checkinLink: z.string().nullable().default(null),
    /** Where the viewer stands at a room on request; null when the room is not on request. */
    request: z.enum(myRoomRequestStateValues).nullable().default(null),
    /** The viewer left with "Ich gehe": only a deliberate check-in brings them back. */
    leftOnPurpose: z.boolean().default(false),
    /** The viewer runs the room and may send announcements to everyone inside. */
    canAnnounce: z.boolean().default(false),
    /** The viewer's shots tonight; null while they are not checked in. */
    roll: z
      .lazy(() => liveRoomRollSchema)
      .nullable()
      .optional(),
  }),
  friends: z.array(liveRoomPersonSchema),
  openPeople: z.array(liveRoomPersonSchema),
  coming: liveRoomComingSchema.optional(),
  /** Tonight's announcements, newest first. */
  announcements: z.array(liveRoomAnnouncementSchema).default([]),
})
export type LiveRoom = z.infer<typeof liveRoomSchema>

export const liveRoomResponseSchema = z.object({ room: liveRoomSchema })
export type LiveRoomResponse = z.infer<typeof liveRoomResponseSchema>

export const liveRoomCheckinSchema = z.union([
  z.object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
    accuracyM: z.number().nonnegative(),
    /** Sent by the app on its own (no tap); refused quietly after the viewer left the room. */
    auto: z.boolean().optional(),
    /** The three yeses at the door; checks in open until the event ends. */
    consent: roomConsentSchema.optional(),
  }),
  z.object({
    code: z.string().trim().min(4).max(16),
    /** A guest's name, set with the check-in. */
    name: z.string().trim().min(1).max(40).optional(),
    /** Someone without a date of birth confirms they are 16 or older. */
    ageConfirmed: z.boolean().optional(),
    /** A guest's age as typed; 16 or more also confirms being old enough. */
    age: z.number().int().min(10).max(99).optional(),
    /** Whether others in the room see the age next to the name. */
    showAge: z.boolean().optional(),
    /** Checks in open until the event ends; also the yes to being seen by strangers here. */
    open: z.boolean().optional(),
    /** The three yeses at the door; replaces `ageConfirmed` and `open` for current clients. */
    consent: roomConsentSchema.optional(),
  }),
])
export type LiveRoomCheckinInput = z.infer<typeof liveRoomCheckinSchema>

export const liveRoomVisibilitySchema = z.object({
  visibility: z.enum(liveRoomVisibilityValues),
  duration: z.enum(liveRoomDurationValues),
  /** Open needs the person's explicit yes to being seen and approached by strangers here. */
  consent: z.boolean().optional(),
  /** Whether others here may say Hi; left out keeps the earlier choice. */
  acceptsHi: z.boolean().optional(),
})
export type LiveRoomVisibilityInput = z.infer<typeof liveRoomVisibilitySchema>

export const liveRoomSelfieResponseSchema = z.object({ selfieUrl: z.string() })
export type LiveRoomSelfieResponse = z.infer<typeof liveRoomSelfieResponseSchema>

export const liveRoomWaveSchema = z.object({
  userId: z.string().uuid(),
  compliment: z.enum(liveRoomComplimentValues).optional(),
  /** A compliment in the sender's own words. */
  complimentText: z.string().trim().min(1).max(80).optional(),
})
export type LiveRoomWaveInput = z.infer<typeof liveRoomWaveSchema>

export const liveRoomWaveResponseSchema = z.object({
  state: z.enum(['sent', 'mutual']),
  conversationId: z.string().uuid().nullable(),
})
export type LiveRoomWaveResponse = z.infer<typeof liveRoomWaveResponseSchema>

export const liveRoomPeopleTabValues = ['all', 'friends', 'his', 'new'] as const
export type LiveRoomPeopleTab = (typeof liveRoomPeopleTabValues)[number]

export const liveRoomPeopleQuerySchema = z.object({
  tab: z.enum(liveRoomPeopleTabValues).default('all'),
  offset: z.coerce.number().int().min(0).max(5000).default(0),
})

/** One page of the people the viewer may see in a room; `total` counts every page. */
export const liveRoomPeoplePageSchema = z.object({
  people: z.array(liveRoomPersonSchema),
  total: z.number().int().nonnegative(),
  nextOffset: z.number().int().nullable(),
})
export type LiveRoomPeoplePage = z.infer<typeof liveRoomPeoplePageSchema>

export const liveRoomPersonResponseSchema = z.object({ person: liveRoomPersonSchema.nullable() })

/** Shortest name query the room search answers. */
export const LIVE_ROOM_SEARCH_MIN_LENGTH = 2

/**
 * Searching the people of a room by name: from two characters, matched against the start of any
 * name part, ignoring case and accents.
 */
export const liveRoomSearchQuerySchema = z.object({
  q: z.string().trim().min(LIVE_ROOM_SEARCH_MIN_LENGTH).max(60),
  offset: z.coerce.number().int().min(0).max(1000).default(0),
})
export type LiveRoomSearchQuery = z.infer<typeof liveRoomSearchQuerySchema>

/** Reporting someone from a Live Room: to the moderators, and a guest leaves the room at once. */
export const liveRoomReportSchema = z.object({
  userId: z.string().uuid(),
  reason: z.enum(reportReasonValues),
  details: z.string().trim().max(500).optional(),
})
export type LiveRoomReportInput = z.infer<typeof liveRoomReportSchema>

export const liveRoomShareLinkSchema = z.object({ code: z.string(), url: z.string() })
export type LiveRoomShareLink = z.infer<typeof liveRoomShareLinkSchema>

export const liveRoomShareTargetSchema = z.object({ eventId: z.string().uuid() })

/** What the check-in QR code leads to, readable without an account. */
export const liveRoomCheckinTargetSchema = z.object({
  event: liveRoomEventSchema,
  open: z.boolean(),
  here: z.number().int().nonnegative(),
  /** Up to three pictures of people open in the room, as a teaser before checking in. */
  faces: z.array(z.string()),
})
export type LiveRoomCheckinTarget = z.infer<typeof liveRoomCheckinTargetSchema>

/** How far people got in an event's Live Room, for its organizer: counts, never names. */
export const liveRoomFunnelCountsSchema = z.object({
  scans: z.number().int().nonnegative(),
  joins: z.number().int().nonnegative(),
  guestJoins: z.number().int().nonnegative(),
  his: z.number().int().nonnegative(),
  mutuals: z.number().int().nonnegative(),
  accounts: z.number().int().nonnegative(),
})
export type LiveRoomFunnelCounts = z.infer<typeof liveRoomFunnelCountsSchema>

/** A Live Room as anyone with the event link sees it: counts and the people who are open. */
export const publicLiveRoomSchema = z.object({
  event: liveRoomEventSchema,
  mode: z.enum(liveRoomModeValues),
  open: z.boolean(),
  live: z.boolean(),
  counts: z.object({ here: z.number().int().nonnegative(), open: z.number().int().nonnegative() }),
  people: z.array(
    z.object({
      userId: z.string().uuid(),
      firstName: z.string(),
      age: z.number().int().nullable(),
      picture: z.string().nullable(),
    }),
  ),
})
export type PublicLiveRoom = z.infer<typeof publicLiveRoomSchema>

/** A point the app knows the person is at, for the Live Rooms nearby before signing in. */
export const nearbyLiveRoomsQuerySchema = z.object({
  lat: z.coerce.number().min(-90).max(90),
  lng: z.coerce.number().min(-180).max(180),
  /** How exact the fix is in metres; a coarse fix finds rooms but may not check in by location. */
  accuracyM: z.coerce.number().nonnegative().max(5000).optional(),
})
export type NearbyLiveRoomsQuery = z.infer<typeof nearbyLiveRoomsQuerySchema>

/** An open Live Room whose check-in area covers a point, as anyone sees it without an account. */
export const nearbyLiveRoomSchema = z.object({
  eventId: z.string().uuid(),
  title: z.string(),
  venueName: z.string().nullable(),
  /** The event's cover, or the user room's cover photo. */
  coverUrl: z.string().nullable(),
  /** People checked in right now. */
  here: z.number().int().nonnegative(),
  distanceM: z.number().nonnegative(),
  live: z.boolean(),
  /** 16, or 18 at an 18+ event: the age from which people are open and say Hi there. */
  openAge: z.number().int(),
  /** People get in only once the host accepted their request. */
  onRequest: z.boolean(),
  /**
   * `location`: the fix is close and exact enough to check in with it. `qr`: near the room, but
   * checking in needs its code (or a better fix).
   */
  checkin: z.enum(['location', 'qr']),
})
export type NearbyLiveRoom = z.infer<typeof nearbyLiveRoomSchema>

/** Open Live Rooms around a point, nearest first; at most five. */
export const nearbyLiveRoomsResponseSchema = z.object({ rooms: z.array(nearbyLiveRoomSchema) })
export type NearbyLiveRoomsResponse = z.infer<typeof nearbyLiveRoomsResponseSchema>

/** Live events around a point the viewer is not checked in at yet, for the "Bist du da?" nudge. */
export const liveRoomNearbyResponseSchema = z.object({
  events: z.array(
    z.object({
      eventId: z.string().uuid(),
      title: z.string(),
      venueName: z.string().nullable(),
      here: z.number().int().nonnegative(),
    }),
  ),
})
export type LiveRoomNearbyResponse = z.infer<typeof liveRoomNearbyResponseSchema>

export const eventCheckinCodeResponseSchema = z.object({ code: z.string(), url: z.string() })
export type EventCheckinCodeResponse = z.infer<typeof eventCheckinCodeResponseSchema>

/** A venue's Live Room as the dashboard sees it: its QR code and tonight's room, if one runs. */
export const venueRoomStatusSchema = z.object({
  /** The room running tonight; null when nobody opened one yet or it ended. */
  eventId: z.string().uuid().nullable(),
  /** People checked in right now. */
  here: z.number().int().nonnegative(),
  code: z.string(),
  url: z.string(),
  /** Tonight's announcements, newest first. */
  announcements: z.array(liveRoomAnnouncementSchema),
})
export type VenueRoomStatus = z.infer<typeof venueRoomStatusSchema>

/** The Live Room of an organizer's event as the dashboard sees it before deleting or ending it. */
export const eventLiveRoomStatusSchema = z.object({
  /** People checked in right now. */
  here: z.number().int().nonnegative(),
  /** The organizer ended the room; nobody checks in again. */
  closed: z.boolean(),
})
export type EventLiveRoomStatus = z.infer<typeof eventLiveRoomStatusSchema>

/** What ending a Live Room did: how many people were checked out and told. */
export const eventLiveRoomEndResponseSchema = z.object({
  checkedOut: z.number().int().nonnegative(),
})
export type EventLiveRoomEndResponse = z.infer<typeof eventLiveRoomEndResponseSchema>

export const liveMapFaceSchema = z.object({
  userId: z.string().uuid(),
  firstName: z.string(),
  avatarUrl: z.string().nullable(),
})
export type LiveMapFace = z.infer<typeof liveMapFaceSchema>

/**
 * What runs right now in an area, for the live map: organizer events that are live (with or
 * without a room) and user rooms the viewer may see; `visibility` is `public` for events.
 */
export const liveHotspotsResponseSchema = z.object({
  rooms: z.array(
    z.object({
      eventId: z.string().uuid(),
      kind: z.enum(['event', 'room']),
      visibility: z.enum(userRoomVisibilityValues),
      category: z.enum(liveRoomCategoryValues),
      title: z.string(),
      lat: z.number(),
      lng: z.number(),
      here: z.number().int().nonnegative(),
      open: z.number().int().nonnegative(),
      live: z.boolean(),
      imageUrl: z.string().nullable(),
      /** The organizer event's cover, or the user room's cover photo. */
      coverUrl: z.string().nullable(),
      /** The event has a Live Room people can check in to. */
      hasRoom: z.boolean(),
      moments: z.number().int().nonnegative(),
      /** Up to three friends of the viewer who are checked in and not hidden. */
      friendFaces: z.array(liveMapFaceSchema).max(3),
      /** A room a venue's QR opened, shown on the venue. */
      venueRoom: z.boolean().default(false),
    }),
  ),
})
export type LiveHotspotsResponse = z.infer<typeof liveHotspotsResponseSchema>

/** Who sees a moment: everyone in the room, the author's friends, or the author's group. */
export const liveRoomMomentAudienceValues = ['open', 'friends', 'group'] as const
export type LiveRoomMomentAudience = (typeof liveRoomMomentAudienceValues)[number]

/** Shots per person and event; each moment taken in a room counts, whatever it is shared with. */
export const LIVE_ROOM_SHOTS_PER_EVENT = 12

/**
 * The viewer's shots at an event: how many are left, and how many photos of their group (or their
 * own, without a group) wait to develop. Nothing of them shows before `revealAt`.
 */
export const liveRoomRollSchema = z.object({
  shotsLeft: z.number().int().nonnegative(),
  shotsTaken: z.number().int().nonnegative(),
  /** Photos of the viewer's group, or their own without a group, that have not developed yet. */
  waiting: z.number().int().nonnegative(),
  /** `group` while the viewer is in a group at the event, else `own`. */
  sharedWith: z.enum(['group', 'own']),
  /** When tonight's shots develop: 10:00 the next morning, Vienna time. */
  revealAt: z.string(),
})
export type LiveRoomRoll = z.infer<typeof liveRoomRollSchema>

/** A photo shared in a Live Room, as one viewer may see it. */
export const liveRoomMomentSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  firstName: z.string(),
  avatarUrl: z.string().nullable(),
  /** A signed address valid for about an hour: the photo, or the still of a clip. */
  photoUrl: z.string(),
  kind: z.enum(['photo', 'video']).default('photo'),
  /** The clip itself for a video moment, signed like the photo. */
  videoUrl: z.string().nullable().default(null),
  durationMs: z.number().int().nullable().default(null),
  /** A shot only its author sees (see `roll`) is listed as `group`. */
  audience: z.enum(liveRoomMomentAudienceValues),
  /**
   * Set on a disposable shot that developed the morning after: `group` for the group's shots,
   * `own` for those of someone without a group, which only they see. Null for older moments.
   * The audience of a shot never changes.
   */
  roll: z.enum(['group', 'own']).nullable().optional(),
  mine: z.boolean(),
  /** The author is an accepted friend of the viewer. */
  byFriend: z.boolean().default(false),
  /** The viewer keeps it in their own album. */
  saved: z.boolean().default(false),
  /** The viewer has watched it already. */
  seen: z.boolean().default(false),
  /** How many people watched it; only on the viewer's own moments. */
  viewCount: z.number().int().nullable().default(null),
  createdAt: z.string(),
})
export type LiveRoomMoment = z.infer<typeof liveRoomMomentSchema>

/** After a shot: the moments the viewer may see (the new shot is not among them) and the shots left. */
export const liveRoomShotResponseSchema = z.object({
  moments: z.array(liveRoomMomentSchema),
  roll: liveRoomRollSchema,
})
export type LiveRoomShotResponse = z.infer<typeof liveRoomShotResponseSchema>

/** A room the viewer was in, ready to look back at until its recap closes. */
export const liveRoomRecapSummarySchema = z.object({
  placeId: z.string().uuid(),
  eventId: z.string().uuid(),
  title: z.string(),
  imageUrl: z.string().nullable(),
  endTime: z.string(),
  openUntil: z.string(),
  people: z.number().int().nonnegative(),
  moments: z.number().int().nonnegative(),
})
export type LiveRoomRecapSummary = z.infer<typeof liveRoomRecapSummarySchema>

export const liveRoomRecapsResponseSchema = z.object({
  recaps: z.array(liveRoomRecapSummarySchema),
})

export const liveRoomRecapPersonSchema = z.object({
  userId: z.string().uuid(),
  displayName: z.string().nullable(),
  username: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  isFriend: z.boolean(),
  /** False when the person had Hi switched off in the room. */
  acceptsHi: z.boolean(),
  wave: z.enum(liveRoomWaveStateValues),
  conversationId: z.string().uuid().nullable(),
})
export type LiveRoomRecapPerson = z.infer<typeof liveRoomRecapPersonSchema>

/** The day after: who showed themselves, the moments, and until when a Hi still works. */
export const liveRoomRecapSchema = z.object({
  placeId: z.string().uuid(),
  eventId: z.string().uuid(),
  title: z.string(),
  imageUrl: z.string().nullable(),
  endTime: z.string(),
  openUntil: z.string(),
  people: z.array(liveRoomRecapPersonSchema),
  moments: z.array(liveRoomMomentSchema),
})
export type LiveRoomRecap = z.infer<typeof liveRoomRecapSchema>

/**
 * A look at an event's recap before opening it, only for someone who was in the room: counts, a
 * few faces of people who were open and a few open moments to show blurred.
 */
export const liveRoomRecapTeaserSchema = z.object({
  placeId: z.string().uuid(),
  eventId: z.string().uuid(),
  title: z.string(),
  endTime: z.string(),
  openUntil: z.string(),
  people: z.number().int().nonnegative(),
  moments: z.number().int().nonnegative(),
  faces: z.array(
    z.object({
      userId: z.string().uuid(),
      firstName: z.string(),
      avatarUrl: z.string().nullable(),
    }),
  ),
  previews: z.array(z.string()),
  /** The viewer was open in the room and may still say Hi to the people in the recap. */
  canSayHi: z.boolean(),
})
export type LiveRoomRecapTeaser = z.infer<typeof liveRoomRecapTeaserSchema>

/** `null` when the viewer was not there or the recap is no longer open. */
export const liveRoomRecapTeaserResponseSchema = z.object({
  recap: liveRoomRecapTeaserSchema.nullable(),
})

/** Switches the recap e-mails off (or back on) with the token from the link in one of them. */
export const recapEmailsRequestSchema = z.object({
  token: z.string().min(1).max(200),
  subscribed: z.boolean().default(false),
})
export type RecapEmailsRequest = z.input<typeof recapEmailsRequestSchema>

export const recapEmailsResponseSchema = z.object({ subscribed: z.boolean() })
export type RecapEmailsResponse = z.infer<typeof recapEmailsResponseSchema>

/** One event's moments the viewer kept. */
export const savedMomentGroupSchema = z.object({
  eventId: z.string().uuid(),
  title: z.string(),
  startTime: z.string(),
  moments: z.array(liveRoomMomentSchema),
})
export type SavedMomentGroup = z.infer<typeof savedMomentGroupSchema>

export const savedMomentsResponseSchema = z.object({ groups: z.array(savedMomentGroupSchema) })

export const liveRoomMomentsResponseSchema = z.object({ moments: z.array(liveRoomMomentSchema) })
export type LiveRoomMomentsResponse = z.infer<typeof liveRoomMomentsResponseSchema>

/** A party going on right now where friends of the viewer shared moments, newest first. */
export const friendPartySchema = z.object({
  eventId: z.string().uuid(),
  title: z.string(),
  moments: z.array(liveRoomMomentSchema),
})
export type FriendParty = z.infer<typeof friendPartySchema>

export const friendPartiesResponseSchema = z.object({ parties: z.array(friendPartySchema) })
export type FriendPartiesResponse = z.infer<typeof friendPartiesResponseSchema>

/** A member of someone's group for tonight at an event. */
export const liveRoomGroupMemberSchema = z.object({
  userId: z.string().uuid(),
  firstName: z.string(),
  avatarUrl: z.string().nullable(),
  /** Checked in to this event's Live Room right now. */
  here: z.boolean(),
  isGuest: z.boolean(),
  isMe: z.boolean(),
})
export type LiveRoomGroupMember = z.infer<typeof liveRoomGroupMemberSchema>

/**
 * The group for tonight: a plan linked to the event, created from the Live Room. Its code lets
 * people join with one scan, which also checks them in to the room.
 */
/** An invitation from someone in the room to join their group for the evening. */
export const liveRoomGroupInviteSchema = z.object({
  id: z.string().uuid(),
  planId: z.string().uuid(),
  groupName: z.string(),
  from: z.object({
    userId: z.string().uuid(),
    firstName: z.string(),
    avatarUrl: z.string().nullable(),
  }),
  memberCount: z.number().int().nonnegative(),
  createdAt: z.string(),
})
export type LiveRoomGroupInvite = z.infer<typeof liveRoomGroupInviteSchema>

/** Invites someone in the room to the viewer's group; they join only once they accept. */
export const liveRoomGroupInviteCreateSchema = z.object({ userId: z.string().uuid() })

export const liveRoomGroupSchema = z.object({
  planId: z.string().uuid(),
  conversationId: z.string().uuid().nullable(),
  name: z.string(),
  /** Short code that checks someone in and adds them to the group at once. */
  code: z.string(),
  /** Web link carrying the code, for the QR on screen and for sending. */
  link: z.string(),
  members: z.array(liveRoomGroupMemberSchema),
  hereCount: z.number().int().nonnegative(),
  isCreator: z.boolean(),
})
export type LiveRoomGroup = z.infer<typeof liveRoomGroupSchema>

export const liveRoomGroupResponseSchema = z.object({ group: liveRoomGroupSchema.nullable() })
export type LiveRoomGroupResponse = z.infer<typeof liveRoomGroupResponseSchema>

export const liveRoomGroupAddSchema = z.object({ userId: z.string().uuid() })
export type LiveRoomGroupAddInput = z.infer<typeof liveRoomGroupAddSchema>

/** At most this many people in one group at an event. */
export const LIVE_ROOM_GROUP_MAX_MEMBERS = 8

/**
 * What adding someone did: `added` someone who was in no group (they are told and may leave),
 * `invited` someone already in another group (they decide), or `member` when they already were.
 */
export const liveRoomGroupAddOutcomeValues = ['added', 'invited', 'member'] as const
export type LiveRoomGroupAddOutcome = (typeof liveRoomGroupAddOutcomeValues)[number]

/** The viewer's group after adding someone; a viewer without one got a new group. */
export const liveRoomGroupAddResponseSchema = z.object({
  group: liveRoomGroupSchema,
  outcome: z.enum(liveRoomGroupAddOutcomeValues),
  /** The person as the viewer now sees them in the room. */
  person: liveRoomPersonSchema.nullable(),
})
export type LiveRoomGroupAddResponse = z.infer<typeof liveRoomGroupAddResponseSchema>

/**
 * Why someone in the room is suggested for the viewer's group, strongest first: a friend, a friend
 * of friends, someone who came with one of the viewer's friends, or someone who checked in within
 * a few minutes of the viewer or their group.
 */
export const liveRoomGroupSuggestionReasonValues = [
  'friend',
  'mutual_friends',
  'friends_group',
  'arrived_together',
] as const
export type LiveRoomGroupSuggestionReason = (typeof liveRoomGroupSuggestionReasonValues)[number]

/** Someone in the room the viewer probably knows, offered for "Dazuholen". */
export const liveRoomGroupSuggestionSchema = z.object({
  person: liveRoomPersonSchema,
  reason: z.enum(liveRoomGroupSuggestionReasonValues),
  /** Friends the viewer and this person share. */
  mutualFriends: z.number().int().nonnegative(),
})
export type LiveRoomGroupSuggestion = z.infer<typeof liveRoomGroupSuggestionSchema>

export const liveRoomGroupSuggestionsResponseSchema = z.object({
  suggestions: z.array(liveRoomGroupSuggestionSchema),
})
export type LiveRoomGroupSuggestionsResponse = z.infer<
  typeof liveRoomGroupSuggestionsResponseSchema
>

/** The open Live Room the account is checked in to right now, to open it right after sign-in. */
export const liveRoomCurrentResponseSchema = z.object({
  room: z
    .object({
      eventId: z.string().uuid(),
      placeId: z.string().uuid(),
      title: z.string(),
    })
    .nullable(),
})
export type LiveRoomCurrentResponse = z.infer<typeof liveRoomCurrentResponseSchema>

/**
 * The code and link that put someone on the viewer's film at an event: their group's, or the
 * viewer's own until a first person scans it and the group starts.
 */
export const liveRoomFilmLinkSchema = z.object({
  code: z.string(),
  link: z.string(),
})
export type LiveRoomFilmLink = z.infer<typeof liveRoomFilmLinkSchema>

/** The unlisted web page of a group's developed film. */
export const liveRoomFilmShareSchema = z.object({ url: z.string() })
export type LiveRoomFilmShare = z.infer<typeof liveRoomFilmShareSchema>

/**
 * A group's developed film as anyone with its link sees it: the event, the group's own name, how
 * many photos it holds and a few blurred frames. The photos themselves are for the members.
 */
export const liveRoomFilmTeaserSchema = z.object({
  title: z.string(),
  startTime: z.string(),
  /** The name the group gave itself; null while it is still named after the event. */
  groupName: z.string().nullable(),
  photoCount: z.number().int().nonnegative(),
  /** Tiny blurred frames as data URIs, on which nothing and nobody can be made out. */
  previews: z.array(z.string()),
})
export type LiveRoomFilmTeaser = z.infer<typeof liveRoomFilmTeaserSchema>

/** A group's developed film as its members see it: the photos, oldest first. */
export const liveRoomFilmSchema = z.object({
  title: z.string(),
  startTime: z.string(),
  groupName: z.string().nullable(),
  photos: z.array(z.object({ id: z.string().uuid(), url: z.string() })),
})
export type LiveRoomFilm = z.infer<typeof liveRoomFilmSchema>
